import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { useApiMutation, useApiQuery } from '@/modules/common';
import { PrinterType } from '@/modules/common/types';
import { useModelAttributes } from '@/modules/models';

import { GcrDetails, GcrUploadUrlData, UpdateGcrBody } from '../types';

export function useGcrsDetails(id: string) {
  const { data: optionsData } = useModelAttributes();
  const queryClient = useQueryClient();

  const { data: printers, isFetching: isFetchingPrinters } = useApiQuery<
    PrinterType[]
  >('/printers', {
    queryKey: ['printers'],
    initialData: [],
  });

  const uploadUrlDataMutation = useApiMutation<void, GcrUploadUrlData>(
    '/gcrs/upload-url',
    {
      method: 'GET',
    }
  );

  const { isFetching, data, isError, error } = useApiQuery<GcrDetails>(
    `/gcrs/${id}`,
    {
      queryKey: ['gcrsDetails', id],
      initialData: null,
    }
  );

  useEffect(() => {
    if (isError) {
      toast.error(error?.message || 'Uh oh! Something went wrong.');
    }
  }, [isError, error]);

  const updateGcrMutation = useApiMutation<UpdateGcrBody, GcrDetails>(
    `/gcrs/${id}`,
    {
      method: 'PATCH',
    },
    {
      onSuccess: (updatedGcr) => {
        queryClient.setQueryData(['gcrsDetails', id], updatedGcr);
      },
    }
  );

  return {
    optionsData: {
      printers,
      ...optionsData,
    },
    isFetching: isFetching || isFetchingPrinters,
    details: data,
    uploadUrlDataMutation,
    updateGcrMutation,
  };
}
